import { useRef } from 'react'
import { useModalBehavior } from '../../../hooks/useModalBehavior'
import './VisibilityConfigDropdown.css'

interface Field {
    key: string
    label: string
    checked: boolean
    disabled?: boolean
}

type Position = 'bottom-right' | 'bottom-left'

interface VisibilityConfigDropdownProps {
    fields: Field[]
    onToggle: (fieldKey: string) => void
    onClose: () => void
    isSharedView?: boolean
    allowedFields?: string[] | null
    position?: Position
    onReset?: (() => void) | null
}

export default function VisibilityConfigDropdown({
    fields = [],
    onToggle,
    onClose,
    isSharedView = false,
    allowedFields = null,
    position = 'bottom-right',
    onReset = null,
}: Readonly<VisibilityConfigDropdownProps>) {
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Use modal behavior hook for click-outside and escape handling
    useModalBehavior({
        modalRef: dropdownRef,
        onClose,
    })

    const handleToggle = (fieldKey: string, disabled?: boolean, isAllowed?: boolean) => {
        // In shared views, only toggle if field is allowed
        if (isSharedView && !isAllowed) {
            console.warn(`Field "${fieldKey}" is not allowed by owner`)
            return
        }
        if (disabled) return
        onToggle(fieldKey)
    }

    return (
        <div ref={dropdownRef} className={`visibility-config-dropdown ${position}`} onClick={(e) => e.stopPropagation()} role="menu" tabIndex={0}>
            <div className="config-header">Personalizar campos</div>

            <div className="config-options">
                {fields.length === 0 ? (
                    <div className="config-empty">No hay campos configurables</div>
                ) : (
                    fields.map((field) => {
                        // In shared views, check if field is allowed
                        const isAllowed = !isSharedView || !allowedFields || allowedFields.includes(field.key)
                        const isDisabled = field.disabled || (isSharedView && !isAllowed)

                        return (
                            <label
                                key={field.key}
                                className={`config-option ${isDisabled ? 'disabled' : ''}`}
                                title={isSharedView && !isAllowed ? 'Campo no permitido por el propietario' : ''}
                            >
                                {isAllowed ? (
                                    <input
                                        type="checkbox"
                                        checked={field.checked}
                                        onChange={() => handleToggle(field.key, field.disabled, isAllowed)}
                                        disabled={isDisabled}
                                    />
                                ) : (
                                    <span className="config-not-allowed">🔒</span>
                                )}
                                <span className="config-label">{field.label}</span>
                                {field.disabled && isAllowed && (
                                    <span className="config-hint" title="Este campo siempre está visible">
                                        (obligatorio)
                                    </span>
                                )}
                            </label>
                        )
                    })
                )}
            </div>

            {isSharedView && (
                <div className="config-footer shared-notice">
                    {allowedFields
                        ? `📚 Puedes personalizar los campos permitidos por el propietario (🔒 = no permitido)`
                        : `👀 Vista compartida (solo lectura)`}
                </div>
            )}

            {!isSharedView && onReset && (
                <div className="config-footer">
                    <button className="config-reset-btn" onClick={onReset} type="button">
                        🔄 Restaurar por defecto
                    </button>
                </div>
            )}
        </div>
    )
}
